import { getOpenAnalystAccountAccessTokenForRoute, getOpenAnalystApiAccessToken } from "@/context/auth/broker"
import { localDevAuthBypass } from "@/context/auth/dev-bypass"
import { openAnalystWebBaseUrl } from "@/utils/openanalyst-hosts"
import type { OnboardingV2Ctx } from "@/pages/onboarding/v2/state"
import { queryOptions } from "@/utils/query-options"

const COMPLETED_KEY = "openanalyst.onboarding.v2.completed"
// Install-level flag: this machine has finished onboarding for SOME account at
// least once. Unlike the per-user marker it is never user-scoped and survives
// sign-out, so a signed-out returning user lands on /login (not onboarding).
const EVER_KEY = "openanalyst.onboarding.v2.ever"
const SETTINGS_KEY = "settings.v3"

function completedKey(userId: string | null | undefined) {
  const id = userId?.trim()
  if (!id) return COMPLETED_KEY
  return `${COMPLETED_KEY}.${id}`
}

function completed(storage: Storage, key: string) {
  return storage.getItem(key) === "true"
}

/** Has this install ever finished onboarding for any account? Survives sign-out. */
export function hasEverOnboardedV2(): boolean {
  if (typeof localStorage === "undefined") return false
  try {
    return localStorage.getItem(EVER_KEY) === "true"
  } catch {
    return false
  }
}

/** Has this browser already completed onboarding v2? localStorage-only fast path. */
export function isOnboardingV2Completed(userId?: string | null): boolean {
  if (typeof localStorage === "undefined") return false
  try {
    const key = completedKey(userId)
    if (completed(localStorage, key)) return true
    // No specific user (signed out / pre-auth): fall back to the install-level
    // flag so a returning user who already onboarded is not forced through it
    // again — they should see /login instead.
    if (!userId?.trim()) return hasEverOnboardedV2()
    if (!completed(localStorage, COMPLETED_KEY)) return false
    localStorage.setItem(key, "true")
    localStorage.removeItem(COMPLETED_KEY)
    return true
  } catch {
    return false
  }
}

/**
 * Mark onboarding v2 as finished for this account on this install. Called both
 * at the end of the new-user flow (LandingScreen) and as soon as a returning
 * user signs in to an account the server reports as already onboarded.
 */
export function markOnboardingV2Completed(userId?: string | null) {
  if (typeof localStorage === "undefined") return
  try {
    localStorage.setItem(completedKey(userId), "true")
    localStorage.setItem(EVER_KEY, "true")
  } catch {
    // ignore
  }
}

/**
 * Read the onboardingV2 flag straight from persisted settings storage.
 * Avoids needing a SettingsProvider in pre-auth scope (no dual-provider risk).
 */
export function isOnboardingV2Enabled(): boolean {
  // Onboarding v2 is ON BY DEFAULT for the production build (new + returning users).
  // It is only disabled when EXPLICITLY opted out (settings.general.onboardingV2 ===
  // false — which the dev OPENANALYST_DEV_DISABLE_ONBOARDING_V2 path injects). A fresh
  // install (no settings yet) → onboarding ON, so first-run users always see the flow.
  // The returning-user path stays seamless: OnboardingGate checks the server's real
  // onboarding status and marks it complete, so finished accounts skip straight in.
  if (typeof localStorage === "undefined") return true
  try {
    const raw = localStorage.getItem(SETTINGS_KEY)
    if (!raw) return true
    const parsed = JSON.parse(raw) as { general?: { onboardingV2?: boolean } }
    return parsed?.general?.onboardingV2 !== false
  } catch {
    return true
  }
}

const authBaseUrl = openAnalystWebBaseUrl()
let configuredFetch: typeof fetch | null = null

export function configureOnboardingV2Fetch(fetcher: typeof fetch | null) {
  configuredFetch = fetcher
}

function authUrl(path: string) {
  return `${authBaseUrl}${path.startsWith("/") ? path : `/${path}`}`
}

function isSmartworkHostedAuthBaseUrl() {
  return /^https:\/\/(smartwork\.(arjun\.tv|openanalyst\.com)|cloud\.openanalyst\.com)$/.test(authBaseUrl)
}

function onboardingAccessToken() {
  return isSmartworkHostedAuthBaseUrl()
    ? getOpenAnalystAccountAccessTokenForRoute() ?? getOpenAnalystApiAccessToken()
    : getOpenAnalystApiAccessToken() ?? getOpenAnalystAccountAccessTokenForRoute()
}

async function parseJson(response: Response) {
  const text = await response.text()
  if (!text) return undefined
  try {
    return JSON.parse(text) as unknown
  } catch {
    return { message: text }
  }
}

async function request<T>(path: string, input?: { method?: string; body?: unknown }) {
  const headers = new Headers()
  if (input?.body !== undefined) headers.set("Content-Type", "application/json")
  const accessToken = onboardingAccessToken()
  if (accessToken) headers.set("Authorization", `Bearer ${accessToken}`)
  const response = await (configuredFetch ?? fetch)(authUrl(path), {
    method: input?.method ?? "GET",
    credentials: "include",
    headers,
    body: input?.body === undefined ? undefined : JSON.stringify(input.body),
  })
  if (!response.ok) {
    const payload = (await parseJson(response)) as { error?: { message?: string }; message?: string } | undefined
    throw new Error(payload?.error?.message || payload?.message || `Request failed with status ${response.status}`)
  }
  return (await parseJson(response)) as T
}

export type OnboardingV2State = {
  userId: string | null
  ctx: Partial<OnboardingV2Ctx>
  currentStep?: string
  updatedAt?: string
}

export async function loadState(userId: string | null | undefined): Promise<OnboardingV2State | null> {
  if (localDevAuthBypass()) return null
  if (!userId) return null
  try {
    return await request<OnboardingV2State>(`/api/v1/onboarding/v2/state`)
  } catch {
    return null
  }
}

export async function saveStep(input: { userId: string | null; ctx: OnboardingV2Ctx; step?: string }) {
  if (localDevAuthBypass()) return
  if (!input.userId) return
  try {
    await request("/api/v1/onboarding/v2/step", {
      method: "POST",
      body: { ctx: input.ctx, step: input.step },
    })
  } catch {
    // best-effort; local state is source of truth until backend lands
  }
}

export async function acknowledge(input: { userId: string | null; onboardingId?: string }) {
  if (localDevAuthBypass()) return
  if (!input.userId) return
  try {
    await request("/api/v1/onboarding/v2/ack", {
      method: "POST",
      body: { onboardingId: input.onboardingId },
    })
  } catch {
    // ignore; v1 ack still runs for now
  }
}

export const onboardingV2StateQueryOptions = (userId: string | null | undefined) =>
  queryOptions({
    queryKey: ["onboarding-v2", "state", userId ?? "anonymous"] as const,
    queryFn: () => loadState(userId),
    staleTime: 30_000,
  })
